import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { FoldingPanel } from '../../../../ui'
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Checkbox,
  CircularProgress,
  Button,
  Typography,
  Box,
} from '@mui/material'
import {
  getMailingConfig,
  toggleApplicationStatus,
  toggleCategoryAllStatus,
} from '../../slices/mailing-config.slice'

const columns = [
  {
    id: 'name',
    label: 'pages.profile.mailing.columns.name',
    width: '30%',
  },
  {
    id: 'description',
    label: 'pages.profile.mailing.columns.description',
    width: '60%',
  },
  {
    id: 'status',
    label: 'pages.profile.mailing.columns.status',
    type: 'checkbox',
    width: '10%',
  },
]

export const MailingConfigAccordions = () => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const [expandedCategories, setExpandedCategories] = useState({})

  const { categories, isLoading, error } = useSelector((state) => state.mailingConfig)

  useEffect(() => {
    dispatch(getMailingConfig())
    if (categories.length > 0) {
      setExpandedCategories((prev) => ({
        ...prev,
        [categories[0].id]: true,
      }))
    }
  }, [dispatch, categories.length])

  const handleStatusChange = (categoryId, applicationId, currentStatus) => {
    const newStatus = !currentStatus
    dispatch(toggleApplicationStatus(categoryId, applicationId, newStatus))
  }

  const handleCategoryToggle = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }))
  }

  const handleCategorySelectAll = (categoryId, selectAll) => {
    dispatch(toggleCategoryAllStatus(categoryId, selectAll))
  }

  const getCategoryStats = (category) => {
    const nonRequiredApps = category.applications.filter((app) => !app.required)
    const selectedCount = nonRequiredApps.filter((app) => app.status).length
    const totalCount = nonRequiredApps.length
    return { selectedCount, totalCount }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>
  }

  if (categories.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <Typography variant="body1">No hay configuraciones de correo disponibles</Typography>
      </div>
    )
  }

  return (
    <div>
      {categories.map((category) => {
        const { selectedCount, totalCount } = getCategoryStats(category)
        const allSelected = selectedCount === totalCount
        const isExpanded = expandedCategories[category.id]

        return (
          <FoldingPanel
            key={category.id}
            isEsxpanded={isExpanded}
            title={
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <span>{category.name}</span>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ marginRight: '16px', marginLeft: '16px' }}
                >
                  <Typography variant="body2" color="textSecondary" style={{ marginRight: '24px' }}>
                    {selectedCount}/{totalCount} seleccionados
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleCategorySelectAll(category.id, !allSelected)
                    }}
                  >
                    {allSelected ? 'Desmarcar todo' : 'Marcar todo'}
                  </Button>
                </Box>
              </Box>
            }
            onToggle={() => handleCategoryToggle(category.id)}
          >
            <Table sx={{ minWidth: 650, tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      style={{
                        paddingBottom: 0,
                        borderBottomWidth: '2px',
                        width: column.width,
                        textAlign: column.type === 'checkbox' ? 'center' : 'left',
                      }}
                    >
                      <b>{formatMessage({ id: column.label })}</b>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {category.applications.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((column) => {
                      switch (column.type) {
                        case 'checkbox':
                          return (
                            <TableCell
                              style={{
                                paddingBottom: '8px',
                                paddingTop: '8px',
                                width: column.width,
                                textAlign: 'center',
                              }}
                              key={column.id}
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Checkbox
                                  checked={!!row[column.id]}
                                  disabled={row.required}
                                  color="primary"
                                  onChange={() =>
                                    handleStatusChange(category.id, row.id, row[column.id])
                                  }
                                  sx={{ padding: '4px' }}
                                />
                                {row.required && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    textAlign="center"
                                  >
                                    (Obligatorio)
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          )
                        default:
                          return (
                            <TableCell
                              style={{
                                paddingBottom: '8px',
                                paddingTop: '8px',
                                width: column.width,
                                wordWrap: 'break-word',
                              }}
                              key={column.id}
                            >
                              {row[column.id]}
                            </TableCell>
                          )
                      }
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </FoldingPanel>
        )
      })}
    </div>
  )
}
